import React, { useEffect, useState } from "react";
import { useNavigate, useParams, NavLink, Outlet } from "react-router-dom";
import { getApp, getToken } from "../api";
import { RESPONSIVE_NAV_CSS, navToggleProps, navToggleStyle } from "../components/navResponsive";

export default function AppDashboard() {
  const { appId } = useParams();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [navOpen, setNavOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!getToken()) { navigate("/login"); return; }
    getApp(appId)
      .then(setApp)
      .catch(() => navigate("/dashboard"))
      .finally(() => setLoading(false));
  }, [appId, navigate]);

  if (loading) return <div style={s.loading}>Loading...</div>;
  if (!app) return null;

  const phonepeEnabled = app.phonepe_enabled;
  const razorpayEnabled = app.razorpay_enabled;

  return (
    <div style={s.page}>
      <style>{RESPONSIVE_NAV_CSS}</style>

      {/* Top nav */}
      <nav style={s.nav}>
        <div style={s.navLeft}>
          <button {...navToggleProps(navOpen, setNavOpen)} style={navToggleStyle}>
            {navOpen ? "✕" : "☰"}
          </button>
          <button onClick={() => navigate("/dashboard")} style={s.backBtn}>← Apps</button>
          <div style={s.appIcon}>{app.name[0].toUpperCase()}</div>
          <span style={s.appName}>{app.name}</span>
          <span style={s.slugChip}>{app.id}</span>
          {phonepeEnabled && (
            <>
              <span style={{ ...s.gwBadge, ...s.ppBadge }}>PhonePe</span>
              <span style={{ ...s.envBadge, ...(app.phonepe_environment === "PROD" ? s.prodBadge : s.uatBadge) }}>
                {app.phonepe_environment}
              </span>
            </>
          )}
          {razorpayEnabled && (
            <>
              <span style={{ ...s.gwBadge, ...s.rzBadge }}>Razorpay</span>
              <span style={{ ...s.envBadge, ...(app.razorpay_environment === "LIVE" ? s.prodBadge : s.uatBadge) }}>
                {app.razorpay_environment}
              </span>
            </>
          )}
        </div>
      </nav>

      <div style={s.layout} className="ar-layout">
        {/* Sidebar — collapsible on narrow screens; tapping a link closes it */}
        <aside
          id="app-sidebar"
          className={navOpen ? "ar-sidebar ar-sidebar-open" : "ar-sidebar"}
          style={s.sidebar}
          onClick={() => setNavOpen(false)}
        >
          {phonepeEnabled && (
            <>
              <div style={s.navGroupLabel}>PhonePe</div>
              <div style={s.navGroup}>
                <NavLink to={`/apps/${appId}`} end style={navStyle}>Summary</NavLink>
                <NavLink to={`/apps/${appId}/subscriptions`} style={navStyle}>Subscriptions</NavLink>
                <NavLink to={`/apps/${appId}/transactions`} style={navStyle}>Transactions</NavLink>
              </div>
            </>
          )}
          {razorpayEnabled && (
            <>
              {phonepeEnabled && <div style={s.navDivider} />}
              <div style={s.navGroupLabel}>Razorpay</div>
              <div style={s.navGroup}>
                <NavLink to={`/apps/${appId}/rz-subscriptions`} style={navStyle}>Subscriptions</NavLink>
                <NavLink to={`/apps/${appId}/rz-payments`} style={navStyle}>Payments</NavLink>
              </div>
            </>
          )}
          <div style={s.navDivider} />
          <NavLink to={`/apps/${appId}/integration-guide`} style={navStyle}>Integration Guide</NavLink>
          <NavLink to={`/apps/${appId}/settings`} style={navStyle}>Settings</NavLink>
        </aside>

        {/* Content area — rendered by child routes */}
        <main style={s.content} className="ar-content">
          <Outlet context={{ app, setApp }} />
        </main>
      </div>
    </div>
  );
}

function navStyle({ isActive }) {
  return {
    display: "block",
    padding: "10px 16px",
    borderRadius: 8,
    textDecoration: "none",
    fontSize: 14,
    fontWeight: isActive ? 600 : 400,
    color: isActive ? "#4f46e5" : "#475569",
    backgroundColor: isActive ? "#ede9fe" : "transparent",
    marginBottom: 2,
  };
}

const s = {
  page: { minHeight: "100vh", backgroundColor: "#f8fafc", fontFamily: "system-ui, sans-serif" },
  loading: { padding: 40, textAlign: "center", color: "#64748b", fontFamily: "system-ui" },
  nav: { display: "flex", alignItems: "center", padding: "14px 24px", backgroundColor: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" },
  navLeft: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  backBtn: { padding: "5px 10px", backgroundColor: "transparent", border: "1px solid #e2e8f0", borderRadius: 6, cursor: "pointer", color: "#475569", fontSize: 13 },
  appIcon: { width: 30, height: 30, borderRadius: 8, backgroundColor: "#ede9fe", color: "#4f46e5", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14 },
  appName: { fontWeight: 600, fontSize: 16, color: "#1e293b" },
  slugChip: { fontSize: 12, color: "#94a3b8", backgroundColor: "#f1f5f9", padding: "2px 8px", borderRadius: 20 },
  gwBadge: { padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600 },
  rzBadge: { backgroundColor: "#fff1f2", color: "#be123c" },
  ppBadge: { backgroundColor: "#eff6ff", color: "#1d4ed8" },
  envBadge: { padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600 },
  uatBadge: { backgroundColor: "#fef9c3", color: "#854d0e" },
  prodBadge: { backgroundColor: "#dcfce7", color: "#166534" },
  layout: { display: "flex", minHeight: "calc(100vh - 57px)" },
  sidebar: { width: 200, padding: "20px 12px", backgroundColor: "#fff", borderRight: "1px solid #f1f5f9", flexShrink: 0, display: "flex", flexDirection: "column" },
  navGroup: { display: "flex", flexDirection: "column" },
  navGroupLabel: { padding: "6px 16px 4px", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" },
  navDivider: { height: 1, backgroundColor: "#f1f5f9", margin: "10px 4px" },
  content: { flex: 1, padding: "32px", overflowY: "auto" },
};
